import React, { useState } from 'react'
import PropTypes from 'prop-types'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField
} from '@mui/material'

const CorruptTimestampModal = ({ open, onClose, onSubmit, vialLabel, filename }) => {
  const [selectedDate, setSelectedDate] = useState('')
  const [hour, setHour] = useState('')
  const [minute, setMinute] = useState('')
  const [error, setError] = useState('')

  const handleSubmit = () => {
    if (!selectedDate) {
      setError('Please select a date')
      return
    }

    if (hour === '' || minute === '') {
      setError('Please enter both hour and minute')
      return
    }

    // Validate hour (0-23)
    const hourNum = parseInt(hour, 10)
    if (isNaN(hourNum) || hourNum < 0 || hourNum > 23) {
      setError('Hour must be between 0 and 23')
      return
    }

    // Validate minute (0-59)
    const minuteNum = parseInt(minute, 10)
    if (isNaN(minuteNum) || minuteNum < 0 || minuteNum > 59) {
      setError('Minute must be between 0 and 59')
      return
    }

    // Parse the date input (YYYY-MM-DD format) in UTC to avoid timezone issues
    const dateStr = `${selectedDate}T${String(hourNum).padStart(2, '0')}:${String(minuteNum).padStart(2, '0')}:00`
    const dateObj = new Date(dateStr)
    if (isNaN(dateObj.getTime())) {
      setError('Invalid date format')
      return
    }

    // Validate that the datetime is not in the future
    const now = new Date()
    if (dateObj > now) {
      setError('Date and time cannot be in the future')
      return
    }

    // Validate reasonable date range
    const minDate = new Date('1990-01-01T00:00:00')
    if (dateObj < minDate) {
      setError('Date is too far in the past')
      return
    }

    // Format as ISO string (YYYY-MM-DDTHH:mm:ssZ)
    const formattedDateTime = dateObj.toISOString()
    onSubmit(formattedDateTime)
    handleClose()
  }

  const handleClose = () => {
    setSelectedDate('')
    setHour('')
    setMinute('')
    setError('')
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Corrupt Timestamp Detected</Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Typography variant="body1" gutterBottom>
            The file <strong>{filename}</strong> from vial <strong>{vialLabel}</strong> has a corrupt timestamp (1999-11-30).
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ mt: 2 }}>
            The data is valid, but we need you to manually enter the correct date and time for this record.
          </Typography>
          <Box sx={{ mt: 3 }}>
            <TextField
              label="Date"
              type="date"
              value={selectedDate}
              onChange={(e) => {
                setSelectedDate(e.target.value)
                setError('')
              }}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                max: new Date().toISOString().split('T')[0]
              }}
            />
          </Box>
          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <TextField
              label="Hour (0-23)"
              value={hour}
              onChange={(e) => {
                const value = e.target.value
                if (value === '' || (/^\d{0,2}$/.test(value) && parseInt(value) <= 23)) {
                  setHour(value)
                  setError('')
                }
              }}
              type="number"
              inputProps={{ min: 0, max: 23 }}
              fullWidth
              placeholder="HH"
            />
            <TextField
              label="Minute (0-59)"
              value={minute}
              onChange={(e) => {
                const value = e.target.value
                if (value === '' || (/^\d{0,2}$/.test(value) && parseInt(value) <= 59)) {
                  setMinute(value)
                  setError('')
                }
              }}
              type="number"
              inputProps={{ min: 0, max: 59 }}
              fullWidth
              placeholder="MM"
            />
          </Box>
          {error && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          Cancel
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained">
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  )
}

CorruptTimestampModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  vialLabel: PropTypes.string,
  filename: PropTypes.string
}

export default CorruptTimestampModal
